/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer, useLocalStore } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { ResourceValidationResult, validateTranslatorResource } from 'src/languageStudio/core/utils/demosUtils';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';
import { TranslationConfigurationPage } from 'src/translation/components/TranslationConfigurationPage';

const messages = defineMessages({
    pageTitle: { id: 'translation.translationSettingsPage.pageTitle', defaultMessage: 'Translation settings' },
    goToSettings: { id: 'translation.translationSettingsPage.goToSettings', defaultMessage: 'Go to Settings' },
    pageSubtitle: {
        id: 'translation.translationSettingsPage.pageSubtitle',
        defaultMessage: 'Set your Azure resource and your default document location for your translation jobs. '
    }
});

const theme = getScopedTheme('TranslationSettingsPage');
const headerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { borderBottom: theme.border, paddingLeft: '28px', paddingBottom: '13px' }
});
const pageStepStyles = classNamesFunction<IStackProps, IStackItemStyles>()({ root: { paddingTop: '21px', position: 'relative' } });

export const TranslationSettingsPage = observer(() => {
    const { rootStore, dispatcher } = useGlobalState();
    const { subscriptionId, csAccountStore, translatorStorageAccount } = rootStore.azureStore.session;

    const { localize } = useLocalization();

    const resourceTracker = useProgressTracker();
    const storageAccountTracker = useProgressTracker();

    const resourcesStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);
    const azureStorageAccountsStoreHouse = useLocalStore<ResourceStoreHouse<AzureStorageAccountStore>>(createResourceStoreHouse);

    const [isSaveDisabled, setIsSaveDisabled] = React.useState<boolean>(true);
    const [selectedStorageAccount, setSelectedStorageAccount] = React.useState<string>(null);
    const [isValidResource, setIsValidResource] = React.useState<ResourceValidationResult>({ isValid: true });

    React.useEffect(() => {
        if (csAccountStore?.id) {
            setIsValidResource(validateTranslatorResource({ localize, resource: csAccountStore }));
        }
    }, [csAccountStore?.id]);

    React.useEffect(() => {
        if (subscriptionId && isValidResource.isValid && rootStore.azureStore.session.userTokens.armToken) {
            fetchStorageAccounts();
        }
    }, [subscriptionId, rootStore.azureStore.session.userTokens.armToken, isValidResource.isValid]);

    React.useEffect(() => {
        setSelectedStorageAccount(translatorStorageAccount);
    }, [translatorStorageAccount]);

    React.useEffect(() => {
        if (subscriptionId) {
            fetchTranslatorResources();
        }
    }, [subscriptionId]);

    const fetchStorageAccounts = () => {
        dispatcher.dispatch('getAzureStorageAccountsBySubscriptionId', {
            toastData: {},
            id: subscriptionId,
            tracker: storageAccountTracker,
            localStore: azureStorageAccountsStoreHouse
        });
    };

    const fetchTranslatorResources = () => {
        dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
            toastData: {},
            id: subscriptionId,
            tracker: resourceTracker,
            localStoreHouse: resourcesStoreHouse,
            filterPredicate: AzureResourcesStrategies.translatorResourceStrategy
        });
    };

    const onResourceChange = (resourceId: string) => {
        dispatcher.dispatch('switchAzureCsAccountInSession', {
            toastData: {},
            tracker: resourceTracker,
            csAccountStore: resourcesStoreHouse.items.find(r => r.id === resourceId)
        });
        setIsSaveDisabled(false);
    };

    const onStorageAccountChange = (storageAccountName: string) => {
        setSelectedStorageAccount(storageAccountName);
        setIsSaveDisabled(false);
    };

    const saveChanges = () => {
        setIsSaveDisabled(true);
        dispatcher.dispatch('updateTranslatorStorageAccountInSession', { storageAccountName: selectedStorageAccount });
    };

    return (
        <Page pageName="TranslationSettings" title={localize(messages.pageTitle)}>
            <Stack tokens={{ childrenGap: 5 }} styles={headerStyles}>
                <Stack verticalAlign="center" tokens={{ childrenGap: 5 }}>
                    <EditableTitle title={localize(messages.pageTitle)} />
                </Stack>
                <LuisText>{localize(messages.pageSubtitle)}</LuisText>
            </Stack>

            <Stack grow tokens={{ childrenGap: 21 }} styles={isValidResource.isValid && pageStepStyles}>
                <TranslationConfigurationPage
                    onNext={null}
                    onSubmit={null}
                    onDismiss={null}
                    onPrevious={null}
                    activeStep={null}
                    isReturningUser={null}
                    saveChanges={saveChanges}
                    onResourceChange={onResourceChange}
                    selectedResourceStore={csAccountStore}
                    translatorResources={resourcesStoreHouse.items}
                    selectedStorageAccount={selectedStorageAccount}
                    onStorageAccountChange={onStorageAccountChange}
                    onRefreshStorageAccounts={fetchStorageAccounts}
                    storageAccountsError={storageAccountTracker.error}
                    storageAccounts={azureStorageAccountsStoreHouse.items}
                    isSaveDisabled={isSaveDisabled || !selectedStorageAccount || !csAccountStore}
                    isResourcesLoading={resourcesStoreHouse.tracker.isRunning || resourceTracker.isRunning}
                    isStorageAccountsLoading={azureStorageAccountsStoreHouse.tracker.isRunning || storageAccountTracker.isRunning}
                />
            </Stack>
        </Page>
    );
});
